/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, lazy, useContext, useMemo } from "react"
import type { Context, FC, LazyExoticComponent, ReactElement, RefObject } from "react"

import { type QueryKey } from "@tanstack/react-query"

import { useCSSInsertion } from "@/3514/hooks"
import { type TFeedbackModalProps } from "@/3514/modals"
import type { IUserModel } from "@/3514/types"
import { getCssIdentifierFromFileName, renderRichContent } from "@/3514/utils"
import type { TEmptyCallback } from "@/shared/types/functions"

import {
    type TCoachingChatDisclaimerQuery as TDisclaimerQuery,
    type TCoachingChatSeedMutation as TSeedAnswerMutation,
    type TCoachingChatSeedMutationOptions as TSeedAnswerMutationOptions,
    coachingChatQueries as queries,
    useCoachingChatSeedMutation as useSeedMutation
} from "../api"
import {
    CoachingChatConversation as Conversation,
    CoachingChatQuestions as FieldList,
    CoachingChatInsights as Insights
} from "../components"
import type {
    TCoachingChatConversationStarter as TConversationStarter,
    TCoachingChatFormMutationOptions as TFormMutationOptions,
    TCoachingChatConversationMessageSendFunction as TMessageSender,
    TCoachingChatMessageStreamer as TMessageStreamer,
    TCoachingChatModalManager as TModalManager,
    TCoachingChatProgressingMessageBorderHookParams as TStreamingMessageBorderHookParams,
    TCoachingChatProgressingMessageBorderHookReturn as TStreamingMessageBorderHookReturn
} from "../hooks"
import {
    useCoachingChatAbandoner as useAbandoner,
    useCoachingChatApiSwitchReloader as useApiSwitchReloader,
    useCoachingChatAutoscroller as useAutoscroller,
    useCoachingChatBroadcaster as useBroadcaster,
    useCoachingChatCompletor as useChatCompletor,
    useCoachingChatConnectionManager as useConnectionManager,
    useCoachingChatHistoryReceiver as useConversationHistoryReceiver,
    useCoachingChatConversationJoiner as useConversationJoiner,
    useCoachingChatConversationRestartListener as useConversationRestartListener,
    useCoachingChatConversationStarter as useConversationStarter,
    useCoachingChatFeedScroller as useFeedScroller,
    useCoachingChatFormMutationOptions as useFormMutationOptions,
    useCoachingChatInitialLoader as useInitialLoader,
    useCoachingChatConversationMessageSender as useMessageSender,
    useCoachingChatMessageStreamer as useMessageStreamer,
    useCoachingChatModalManager as useModalManager,
    useCoachingChatRestQueriesManager as useRestQueriesManager,
    useCoachingChatSeedsCompletor as useSeedsCompletor,
    useCoachingChatSocketErrorGeneralListener as useSocketErrorGeneralListener,
    useCoachingChatStreamingMessageBorderHook as useStreamingMessageBorder
} from "../hooks"
import {
    ECoachingChatEntityMode as EEntityMode,
    type TCoachingChatConfig as TChatConfig,
    type TCoachingChatGuideModalProps as TGuideModalProps,
    type TCoachingChatInsights as TInsights
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

const Modals: LazyExoticComponent<FC> = lazy(
    (): Promise<{ default: FC }> =>
        import("../modals").then(({ CoachingChatModalsContainer: M }): { default: FC } => ({ default: M }))
)

const { useDisclaimerHook } = queries

const {
    insertionClassesUtils: {
        getChatMessageRichTextClasses,
        getChatTypingIndicatorClassNamesStringForInsertion: getTypingIndicatorCssString
    }
} = uiUtils

type TChatContext = {
    entityId: number
    entityMode: EEntityMode
    chapterId: number
    wasCompleted: boolean
    abandonment: { handleModalClose: TEmptyCallback }
    form: {
        ref: RefObject<HTMLDivElement>
        mutationHook(options: TSeedAnswerMutationOptions): TSeedAnswerMutation
        mutationOptionsHook(entityId: number): TFormMutationOptions
        isRequired: boolean
        title: string
    }
    conversation: {
        ref: RefObject<HTMLDivElement>
        feedRef: RefObject<HTMLDivElement>
        user: Partial<IUserModel>
        availableSummaryCards: Record<string, string>
        feedScrollerHook(formRef: RefObject<HTMLDivElement>): TEmptyCallback
        starterHook(): TConversationStarter
        messageSenderHook(params: { entityId: number }): TMessageSender
        streamerHook(): TMessageStreamer
        streamingBorderHook(params: TStreamingMessageBorderHookParams): TStreamingMessageBorderHookReturn
        renderMessage(content: string): ReactElement
    }
    feedback: {
        handleModalOpen(modalProps: TFeedbackModalProps): void
        handleModalClose: TEmptyCallback
    }
    insights: TInsights
    guide: {
        handleModalOpen(props: Pick<TGuideModalProps, "onSubmit">): void
        handleModalClose: TEmptyCallback
        queryHook(): TDisclaimerQuery
    }
    matrix: { handleModalOpen: TEmptyCallback; handleModalClose(conversationQueryKey: QueryKey): Promise<void> }
}

const ChatContext: Context<TChatContext> = createContext<TChatContext | undefined>(undefined)

function useChatContext(): TChatContext {
    const context: TChatContext = useContext(ChatContext)

    if (!context) {
        throw new Error("useCoachingChatContext must be used within a CoachingChatProvider")
    }

    return context
}

const displayName: string = "CoachingChatProvider"
const chatMessageEditorCssIdentifier: string = getCssIdentifierFromFileName(displayName, "me")

const ChatProvider: FC<{ config: TChatConfig }> = ({
    config: {
        id: entityId,
        ref: entityRef,
        mode: entityMode,
        insights,
        seedQuestions,
        isChatAvailable,
        isChatCompleted: wasChatCompleted,
        availableSummaryCards,
        areSeedQuestionsRequired,
        chapterId,
        conversationId: existingConversationId,
        chatTitle,
        onChatComplete,
        onSeedQuestionsComplete,
        user
    }
}: {
    config: TChatConfig
}): ReactElement => {
    useCSSInsertion({
        cssString: getTypingIndicatorCssString()
    })

    useInitialLoader({
        entityId,
        conversationId: existingConversationId,
        seedQuestions,
        isChatAvailable,
        wasChatCompleted
    })

    useConnectionManager({
        isChatEnabled: isChatAvailable,
        entityId,
        entityMode,
        wasChatCompleted,
        existingConversationId
    })

    useRestQueriesManager({
        entityId,
        entityMode,
        wasChatCompleted,
        existingConversationId
    })

    useAbandoner({ entityId, entityMode, wasChatCompleted, entityRef })

    useSeedsCompletor({ entityId, onComplete: onSeedQuestionsComplete })

    useChatCompletor({ entityId, onComplete: onChatComplete })

    useSocketErrorGeneralListener({ entityId, entityMode, wasChatCompleted })

    useConversationHistoryReceiver({ entityId, entityMode, wasChatCompleted })

    useConversationJoiner({ entityId, entityMode, wasChatCompleted })

    useConversationRestartListener({ entityId, entityMode, wasChatCompleted })

    useBroadcaster({ entityId, entityMode, wasChatCompleted })

    useApiSwitchReloader({ entityId })

    const modalManager: TModalManager = useModalManager({ entityId })

    const autoscroller: ReturnType<typeof useAutoscroller> = useAutoscroller(entityId)

    const providerValue: TChatContext = useMemo(
        (): TChatContext => ({
            entityId,
            entityMode,
            chapterId,
            wasCompleted: wasChatCompleted,
            abandonment: { handleModalClose: modalManager.closeAbandonment.current },
            form: {
                ref: autoscroller.formRef,
                title: chatTitle,
                isRequired: areSeedQuestionsRequired,
                mutationHook: useSeedMutation,
                mutationOptionsHook: useFormMutationOptions
            },
            conversation: {
                ref: autoscroller.chatRef,
                feedRef: autoscroller.feedRef,
                user,
                availableSummaryCards,
                feedScrollerHook: useFeedScroller,
                starterHook: useConversationStarter,
                messageSenderHook: useMessageSender,
                streamerHook: useMessageStreamer,
                streamingBorderHook: useStreamingMessageBorder,
                renderMessage: renderRichContent({
                    identifier: chatMessageEditorCssIdentifier,
                    classes: getChatMessageRichTextClasses(chatMessageEditorCssIdentifier)
                })
            },
            feedback: {
                handleModalOpen: modalManager.openFeedback.current,
                handleModalClose: modalManager.closeFeedback.current
            },
            guide: {
                handleModalOpen: modalManager.openGuide.current,
                handleModalClose: modalManager.closeGuide.current,
                queryHook: useDisclaimerHook
            },
            matrix: {
                handleModalOpen: modalManager.openMatrix.current,
                handleModalClose: modalManager.closeMatrix.current
            },
            insights
        }),
        [
            entityId,
            entityMode,
            chapterId,
            wasChatCompleted,
            chatTitle,
            areSeedQuestionsRequired,
            user,
            availableSummaryCards,
            insights
        ]
    )

    return (
        <ChatContext.Provider value={providerValue} key={entityId}>
            <Insights />

            <FieldList />

            <Conversation />

            <Modals key={entityId} />
        </ChatContext.Provider>
    )
}

ChatProvider.displayName = displayName

export { ChatProvider as CoachingChatProvider, useChatContext as useCoachingChatContext }
